import React, { createContext, useContext, useCallback } from 'react';
import { LensProduct } from '@/features/facility/orders/types';

type ArrivalTimeZoneLabels = {
  noPreference: string;
  morning: string;
  afternoon_14_16: string;
  lateAfternoon_16_18: string;
  evening_18_20: string;
  night_19_21: string;
}

type OrderClassificationLabels = {
  brandNew: string;
  planChange: string;
  prescriptionReplacement: string;
  damageStainReplacement: string;
  damageReplacement: string;
  stainReplacement: string;
  renewal: string;
  lostNonWarranty: string;
};

const arrivalTimeZoneLabels: ArrivalTimeZoneLabels = {
  noPreference: "指定なし",
  morning: "午前中",
  afternoon_14_16: "14時〜16時",
  lateAfternoon_16_18: "16時〜18時",
  evening_18_20: "18時〜20時",
  night_19_21: "19時〜21時"
}

const orderClassificationLabels: OrderClassificationLabels = {
  brandNew: "新規",
  planChange: "プラン変更（片眼⇒両眼の追加レンズ注文）",
  prescriptionReplacement: "処方交換",
  damageStainReplacement: "破損・汚損交換",
  damageReplacement: "破損交換",
  stainReplacement: "汚損交換",
  renewal: "リニューアル",
  lostNonWarranty: "紛失・保証外",
};

type OrderContextType = {
  arrivalTimeZoneLabels: ArrivalTimeZoneLabels;
  orderClassificationLabels: OrderClassificationLabels;
  handleOrderClassificationChange: (value: string | number, setOrderClassification: (value: string) => void, setNewOrderDate: (value: string) => void) => void;
  getProductOptions: (products: LensProduct[]) => { value: string | number; label: string }[];
  handleProductIdChange: (value: string | number, setProductId: (id: number) => void) => void;
};

const OrderContext = createContext<OrderContextType | null>(null);

const OrderProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const handleOrderClassificationChange = useCallback((value: string | number, setOrderClassification: (value: string) => void, setNewOrderDate: (value: string) => void) => {
    const newValue = String(value);
    setOrderClassification(newValue);
    if(newValue === 'brand_new') {
      setNewOrderDate('');
    }
  }, []);

  const getProductOptions = useCallback((products: LensProduct[]) => {
    return products.map(product => ({
      value: product.id,
      label: product.name,
    }));
  }, []);

  const handleProductIdChange = useCallback((value: string | number, setProductId: (id: number) => void) => {
    setProductId(Number(value));
  }, []);

  const value = {
    arrivalTimeZoneLabels,
    orderClassificationLabels,
    handleOrderClassificationChange,
    getProductOptions,
    handleProductIdChange

  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};

const useOrderContext = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrderContext must be used within an OrderProvider');
  }
  return context;
};

export { OrderProvider, useOrderContext };
